import { format, parseISO } from 'date-fns';

import { EErrorCode, MAX_SHIFT_SHORT_NAME_LENGTH } from '@sediwork/shared';
import buildUniqueArray from './buildUniqueArray';

/* Schedule error codes have no shared/ equivalent (unlike EErrorCode.Shift*, which does,
   and is imported above instead of duplicated here) - out of scope for this PR to
   introduce, so they stay as their own frontend-only enum rather than being folded in. */
export enum EScheduleErrorCode {
  SCHEDULE_INVALID_PAYLOAD = 'SCHEDULE_INVALID_PAYLOAD',
  SCHEDULE_INVALID_RULE = 'SCHEDULE_INVALID_RULE',
  SCHEDULE_NOT_AVAILABLE = 'SCHEDULE_NOT_AVAILABLE',
  SCHEDULE_ENTRY_NOT_CREATED = 'SCHEDULE_ENTRY_NOT_CREATED',
  SCHEDULE_CONSTRAINT_ERROR = 'SCHEDULE_CONSTRAINT_ERROR',
  SCHEDULE_EXTEND_TIME_PERIOD_OVERLAP = 'SCHEDULE_EXTEND_TIME_PERIOD_OVERLAP',
  SCHEDULE_EXTEND_HISTORY_OVERLAP = 'SCHEDULE_EXTEND_HISTORY_OVERLAP',
}

type TErrorContext = Readonly<Partial<{
  facilityId: string
  facilityName: string | null
  targetId: string
  timePeriodText: string
  start: string
  end: string
}>>;

export type TApiError = {
  code: EErrorCode | EScheduleErrorCode
  extensions?: TErrorContext
};

/* Note: `parseISO` keeps date-only strings ('YYYY-MM-DD') as local-midnight, matching
   the day component in `format('dd.MM.yyyy')`. Using `new Date(str)` would parse as
   UTC midnight, which shifts the displayed day back by one in any timezone west of UTC. */
const formatGermanDateRange = (start?: string, end?: string): string => {
  if (!start || !end) return '';
  return `vom ${format(parseISO(start), 'dd.MM.yyyy')} bis ${format(parseISO(end), 'dd.MM.yyyy')}`;
};

type TErrorMessage = Readonly<
  Partial<{
    text: string | ((context: TErrorContext) => string)
    type: 'warning' | 'error'
  }>
>;

const errorMessages: Readonly<Record<EErrorCode | EScheduleErrorCode, TErrorMessage>> = {
  [EErrorCode.InvalidPayload]: {},
  [EScheduleErrorCode.SCHEDULE_INVALID_PAYLOAD]: {},
  [EScheduleErrorCode.SCHEDULE_INVALID_RULE]: {
    text: ({ facilityName }) =>
      'Die Rotationseinstellungen für '
      + (facilityName || 'eine der Einrichtungen')
      + ' sind fehlerhaft oder unvollständig.',
    type: 'warning',
  },
  [EScheduleErrorCode.SCHEDULE_NOT_AVAILABLE]: {
    text: 'Arzt ist im Zeitraum nicht verfügbar.',
    type: 'error',
  },
  [EScheduleErrorCode.SCHEDULE_ENTRY_NOT_CREATED]: {
    text: 'Der Eintrag konnte nicht erstellt werden.',
    type: 'warning',
  },
  [EScheduleErrorCode.SCHEDULE_CONSTRAINT_ERROR]: {
    text: ({ facilityName }) =>
      'Einige Einschränkungen für '
      + (facilityName || 'eine der Einrichtungen')
      + ' konnten nicht eingehalten werden (Min-/Max-Werte).',
    type: 'warning',
  },
  /* Both overlap codes are emitted only from `extend-latest-rotation`'s `extended: false`
     branches, which still return the split entries; their only consumer
     (TimePeriods.vue::saveAdjustedEntries) renders the built message after the create and the
     time-period save resolved, and diverts to an error toast otherwise. The wording can
     therefore assert the adjustment succeeded and scope the failure to the extension. */
  [EScheduleErrorCode.SCHEDULE_EXTEND_TIME_PERIOD_OVERLAP]: {
    text: ({
      timePeriodText, start, end,
    }) => {
      const dateRange = formatGermanDateRange(start, end);
      const periodPart = timePeriodText
        ? `bestehendem Zeitraum ${timePeriodText}`
        : 'bestehendem Zeitraum';
      const overlapPart = `${periodPart}${dateRange ? ' ' + dateRange : ''}`;
      return 'Rotationen wurden angepasst. Die Verlängerung konnte wegen einer '
        + `Überschneidung mit ${overlapPart} nicht angewendet werden.`;
    },
    type: 'warning',
  },
  [EScheduleErrorCode.SCHEDULE_EXTEND_HISTORY_OVERLAP]: {
    text: ({
      facilityName, start, end,
    }) => {
      const dateRange = formatGermanDateRange(start, end);
      const rotationPart = facilityName
        ? `Rotation in ${facilityName}`
        : 'Rotation';
      const overlapPart = `${rotationPart}${dateRange ? ' ' + dateRange : ''}`;
      return 'Rotationen wurden angepasst. Die Verlängerung konnte wegen einer '
        + `Überschneidung mit ${overlapPart} nicht angewendet werden.`;
    },
    type: 'warning',
  },
  [EErrorCode.ShiftInvalidDateRange]: {
    text: '"Gültig bis" muss nach "Gültig ab" liegen.',
    type: 'error',
  },
  [EErrorCode.ShiftShortNameTooLong]: {
    text: `Die Abkürzung darf maximal ${MAX_SHIFT_SHORT_NAME_LENGTH} Zeichen lang sein.`,
    type: 'error',
  },
  [EErrorCode.ShiftVersionNoBoundary]: {
    text: 'Mindestens "Gültig ab" oder "Gültig bis" muss angegeben werden.',
    type: 'error',
  },
  [EErrorCode.ShiftInvalidCapacity]: {
    text: '"Max. Kapazität" muss größer oder gleich "Min. Kapazität" sein.',
    type: 'error',
  },
  [EErrorCode.ShiftInvalidPause]: {
    text: 'Die Pause darf nicht negativ sein.',
    type: 'error',
  },
  [EErrorCode.ShiftInvalidEligibleUsers]: {
    text: 'Alle berechtigten Personen müssen zur selben Einrichtung wie der Dienst gehören.',
    type: 'error',
  },
  [EErrorCode.ShiftReorderStale]: {
    text: 'Die Dienstliste hat sich geändert. Bitte laden Sie die Seite neu und versuchen Sie es erneut.',
    type: 'error',
  },
  [EErrorCode.RuleGroupInvalidUsers]: {
    text: 'Alle ausgewählten Mitglieder müssen zur selben Einrichtung wie die Gruppe gehören.',
    type: 'error',
  },
  [EErrorCode.RuleGroupInvalidRules]: {
    text: 'Alle ausgewählten Regeln müssen zur selben Einrichtung wie die Gruppe gehören.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentNoActiveConfig]: {
    text: 'Für diesen Dienst ist an diesem Tag keine Konfiguration aktiv.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentConflictingConfig]: {
    text: 'Für diesen Dienst sind an diesem Tag mehrere Konfigurationen aktiv.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentUserBusy]: {
    text: 'Diese Person ist zu diesem Zeitpunkt bereits verplant.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentUserAbsent]: {
    text: 'Diese Person hat an diesem Tag eine Abwesenheit eingetragen.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentShiftNotActive]: {
    text: 'Dieser Dienst ist an diesem Tag nicht aktiv.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentInvalidTimeRange]: {
    text: 'Die Endzeit muss nach der Startzeit liegen.',
    type: 'error',
  },
  [EErrorCode.ShiftAssignmentInvalidPause]: {
    text: 'Die Pause darf nicht negativ sein.',
    type: 'error',
  },
  [EErrorCode.VacationNotFound]: {
    text: 'Dieser Urlaub existiert nicht mehr.',
    type: 'error',
  },
  /* Same wording the dialog's own validation uses: the server repeats the check, because the
     dialog resolves "today" once and a booking saved after midnight or from another time zone
     reaches the endpoint with a start the client still considered valid. */
  [EErrorCode.VacationPastStart]: {
    text: 'Der Urlaub kann nicht in der Vergangenheit beginnen.',
    type: 'error',
  },
};

type TDirectusErrorBody = Readonly<{
  code?: EErrorCode | EScheduleErrorCode
  errors?: ReadonlyArray<{ extensions?: { code?: EErrorCode | EScheduleErrorCode } }>
}>;

/*
 * Two shapes reach this. The schedule endpoint answers with the code at the top level, while
 * anything raised through `createError` in an extension arrives inside Directus' own envelope
 * (`{ errors: [{ extensions: { code } }] }`) - reading only the flat one left every endpoint
 * exception falling through to the generic message.
 */
export function extractErrorCode(error: unknown): EErrorCode | EScheduleErrorCode | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const data: unknown = (error as { _data?: unknown })._data;
  if (!data || typeof data !== 'object') return undefined;
  const body: TDirectusErrorBody = data;
  return body.code ?? body.errors?.[0]?.extensions?.code;
}

/** Whether the $fetch interceptor already showed a toast for this failure. */
export const wasNotified = (error: unknown): boolean => (
  typeof error === 'object' && error !== null && '_notified' in error && error._notified === true
);

export const buildErrorMessages = (errors: ReadonlyArray<TApiError>) => {
  const messages = errors
    .map((error) => {
      const message = errorMessages[error.code];

      if (!message?.text) {
        return null;
      }

      return typeof message.text === 'function'
        ? {
          ...message,
          text: message.text(error.extensions || {}),
        }
        : message;
    })
    .filter(Boolean);

  return buildUniqueArray(messages);
};
